<template>
  <div>
    <SelectPlanPanel
      v-if="signUpCredentials.plan === null"
      @selectPlan="selectPlan"
    />

    <template v-else>
      <WizardSteps
        :steps="steps"
        :selected-step="selectedStep"
      />

      <SelectProjectTypeStep
        v-if="selectedStep.number === STEP_NUMBER.SELECT_ORGANIZATION"
        :selected-project-type-id="signUpCredentials.project.type"
        @selectProjectType="selectProjectType"
      />

      <div
        :is="fillProjectInformationCompanyName"
        v-else-if="selectedStep.number === STEP_NUMBER.FILL_PROJECT_INFORMATION"
        :init-sign-up-credentials="signUpCredentials"
        @next="handleFillProjectInformationNext"
        @back="handleFillProjectInformationBack"
      />

      <ContractSigningStep
        v-else-if="selectedStep.number === STEP_NUMBER.CONTRACT_SIGNING"
        :selected-project-id="signUpCredentials.projectId"
        :documents="signUpCredentials.contractDocuments"
        @next="setSignedContract"
        @back="goToBack"
      />

      <PaySubscriptionStep
        v-else-if="selectedStep.number === STEP_NUMBER.PAY_SUBSCRIPTION"
        :credit-card="signUpCredentials.creditCard"
        @next="sendPaymentRequest"
        @back="goToBack"
      />

      <DoneStep
        v-else
      />
    </template>

    <TheLogo />

    <TheFooter />

    <BasePageSpinner v-if="isLoading" />
  </div>
</template>

<script>
import TheFooter from './components/TheFooter';
import TheLogo from './components/TheLogo';
import WizardSteps from '@/components/WizardSteps';
import SelectProjectTypeStep from '@/components/SelectProjectTypeStep';
import FillIndividualEntrepreneurProjectInformationStep
  from '@/components/FillProjectInformation/FillIndividualEntrepreneurProjectInformationStep';
import FillIndividualProjectInformationStep
  from '@/components/FillProjectInformation/FillIndividualProjectInformationStep';
import FillLegalProjectInformationStep from '@/components/FillProjectInformation/FillLegalProjectInformationStep';
import PaySubscriptionStep from '@/components/PaySubscriptionStep/index';
import ContractSigningStep from '@/components/ContractSigningStep';
import DoneStep from '@/components/DoneStep';
import SelectPlanPanel from '@/components/SelectPlanPanel/index';
import cloneDeep from 'lodash.clonedeep';

const STEP_NUMBER = {
  SELECT_PLAN: 1,
  SELECT_ORGANIZATION: 2,
  FILL_PROJECT_INFORMATION: 3,
  CONTRACT_SIGNING: 4,
  PAY_SUBSCRIPTION: 5,
  DONE: 6
};

const STEPS = [
  {
    number: STEP_NUMBER.SELECT_PLAN,
    name: 'Выберите тарифный план',
    description: 'Выберите наиболее подходящий тарифный план именно для вышей сферы и размера организации.'
  },
  {
    number: STEP_NUMBER.SELECT_ORGANIZATION,
    name: 'Выбирете тип организации',
    description: 'От этого зависит вид договора и метод расчета.'
  },
  {
    number: STEP_NUMBER.FILL_PROJECT_INFORMATION,
    name: 'Заполните информацию о компании',
    description: 'На основе заполненной формы будет создан договор на обслуживание. Важно заполнить все без ошибок.'
  },
  {
    number: STEP_NUMBER.CONTRACT_SIGNING,
    name: 'Подпишите документы',
    description: 'На основе заполненной формы будет создан договор на обслуживание. Важно заполнить все без ошибок.'
  },
  {
    number: STEP_NUMBER.PAY_SUBSCRIPTION,
    name: 'Оплатите подписку',
    description: 'Оплатите услуги в соответствии с вашим типом организации.'
  },
  {
    number: STEP_NUMBER.DONE,
    name: 'Готово',
    description: 'Финиш. На Вашу почту будет отправлено письмо с дальнейшей инструкцией'
  }
];

export default {
  name: 'SignUpPage',
  components: {
    TheFooter,
    TheLogo,
    WizardSteps,
    SelectProjectTypeStep,
    FillIndividualEntrepreneurProjectInformationStep,
    FillIndividualProjectInformationStep,
    FillLegalProjectInformationStep,
    PaySubscriptionStep,
    ContractSigningStep,
    DoneStep,
    SelectPlanPanel
  },
  data() {
    return {
      isLoading: false,
      steps: STEPS,
      selectedStep: STEPS[0],
      STEP_NUMBER,
      signUpCredentials: {
        project: {
          type: null,
          name: '',
          legalAddress: '',
          addressForCorrespondence: '',
          tin: '',
          checkingAccountIBAN: '',
          bankBicCode: '',
          bankName: '',
          bankAddress: '',
          phone: '',
          firstName: '',
          lastName: '',
          thirdName: '',
          email: '',
          position: '',
          documentTypes: [1]
        },
        projectId: '',
        staff: {
          phone: '',
          email: ''
        },
        documents: [],
        contractDocuments: [],
        place: {
          name: '',
          address: ''
        },
        creditCard: {
          number: '',
          verificationValue: '',
          holder: '',
          expMonth: '',
          expYear: ''
        },
        plan: null
      }
    }
  },
  computed: {
    fillProjectInformationCompanyName() {
       if (this.signUpCredentials.project.type === 1) {
         return 'FillLegalProjectInformationStep';
       } else if (this.signUpCredentials.project.type === 2) {
         return 'FillIndividualEntrepreneurProjectInformationStep';
       }

      return 'FillIndividualProjectInformationStep';
    }
  },
  watch: {
    async selectedStep() {
      document.body.scrollIntoView({
        block: 'start'
      });

      await this.$nextTick();

      this.handleScrollForUpdateWizardStepsPosition();
    },
    'signUpCredentials.plan'(planId, oldPlanId) {
      if (planId !== null && oldPlanId === null) {
        document.addEventListener('scroll', this.handleScrollForUpdateWizardStepsPosition, {
          passive: true,
          capture: true
        });
      }
    }
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    const id = urlParams.get('id');

    if (id !== null) {
      this.selectPlan(Number(id));
    }
  },
  methods: {
    selectPlan(id) {
      this.signUpCredentials.plan = id;

      this.selectedStep = this.steps[1];
    },
    selectProjectType({ id }) {
      this.signUpCredentials.project.type = id;

      this.selectedStep = this.steps[2];
    },
    async handleFillProjectInformationNext(signUpCredentials) {
      if (window.confirm('Вы уверены, что введённые вами данные действительны?')) {
        this.startLoading();

        try {
          const documents = signUpCredentials.documents.map(({ id }) => id);
          const projectId = await fetch(this.$generateAbsolutePath('/project/agreement/create'), {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
            },
            body: JSON.stringify({
              project: signUpCredentials.project,
              staff: signUpCredentials.staff,
              documents
            })
          })
            .then((response) => {
              if (!response.ok) {
                return Promise.reject(response.json());
              }

              return response.json();
            });

          this.setFilledProjectAndContractorInformation(signUpCredentials);

          this.signUpCredentials.projectId = projectId;

          this.selectedStep = this.steps[3];
        } catch (e) {
          console.log(e);
        }

        this.stopLoading();
      }
    },
    setSignedContract(documents) {
      this.signUpCredentials.contractDocuments = documents;

      this.selectedStep = this.steps[4];
    },
    async sendPaymentRequest(payment) {
      this.startLoading();

      try {
        const requestUrl = this.$generateAbsolutePath(`/project/complete/${this.signUpCredentials.projectId}`);
        const agreementDocuments = this.signUpCredentials.contractDocuments.map(({ id }) => id);

        await fetch(requestUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            staff: this.signUpCredentials.staff,
            place: this.signUpCredentials.place,
            plan: this.signUpCredentials.plan,
            payment,
            agreementDocuments
          })
        })
          .then((response) => {
            if (!response.ok) {
              return Promise.reject(response.json());
            }

            return response.json();
          });

        this.selectedStep = this.steps[5];
      } catch (e) {
        console.log(e);
      }

      this.stopLoading();
    },
    handleFillProjectInformationBack(signUpCredentials) {
      this.setFilledProjectAndContractorInformation(signUpCredentials);

      this.goToBack();
    },
    setFilledProjectAndContractorInformation({ project, staff, place, documents }) {
      this.signUpCredentials.project = project;
      this.signUpCredentials.staff = staff;
      this.signUpCredentials.place = place;
      this.signUpCredentials.documents = documents;
    },
    goToBack() {
      switch (this.selectedStep.number) {
        case STEP_NUMBER.FILL_PROJECT_INFORMATION: {
          this.selectedStep = this.steps[1];

          break;
        }

        case STEP_NUMBER.CONTRACT_SIGNING: {
          this.selectedStep = this.steps[2];

          break;
        }

        case STEP_NUMBER.PAY_SUBSCRIPTION: {
          this.selectedStep = this.steps[3];

          break;
        }

        case STEP_NUMBER.DONE: {
          this.selectedStep = this.steps[4];

          break;
        }

      }
    },
    handleScrollForUpdateWizardStepsPosition() {
      const wizardStepsContainer = document.getElementById('wizard-steps');
      const stepContainer = document.getElementById('step-container');

      if (wizardStepsContainer !== null && stepContainer !== null) {
        const stepContainerRect = stepContainer.getBoundingClientRect();

        let translateYInPixels = Math.min(stepContainerRect.bottom - window.outerHeight, 0);

        Object.assign(wizardStepsContainer.style, {
          transform: `translateY(${translateYInPixels}px)`,
          'max-height': `${stepContainerRect.height}px`
        });
      }
    },
    startLoading() {
      this.isLoading = true;

      document.body.classList.add('overflow-hidden');
    },
    stopLoading() {
      this.isLoading = false;

      document.body.classList.remove('overflow-hidden');
    }
  }
};
</script>

<style>
  @import "../../css/styles.css";
  @import "../../css/media.css";
</style>
